/**
 * Self-test suite for the spam-report flow (signed report + cooldown
 * ledger + report-and-block coordinator).
 *
 * Five groups — mirrors `kaether_for_ios/Stoat/E2EE/SpamReportSelfTest.swift`:
 *
 *   A. Codec — sign + verify round-trip, encode + decode round-trip,
 *      prefix non-collision with siblings, signature tampering detection,
 *      structural rejections (self-report, hash length, schema version).
 *
 *   B. computeMessageIdHash — deterministic from same messageId, differs
 *      across distinct messageIds.
 *
 *   C. Store cooldown — fresh store admits reports, ten-in-window
 *      saturation, window-expiry restoration, recordReport returns false
 *      when saturated.
 *
 *   D. Coordinator — happy path returns both payloads + installs block;
 *      cooldown throws + leaves no state mutation; self-report throws;
 *      block payload reason is "spamFlag"; messageIdHash equals
 *      SHA256(messageId).
 *
 *   E. Hardening invariants — SpamReportPayload has NO free-text reason
 *      field (runtime Object.keys() check); codec NEVER serialises raw
 *      messageId (only the hash); reportedAt is the LOCAL reporter clock.
 *
 * All store cases use isolated IndexedDB database names so the suite is
 * safe to run on every dev launch without polluting production state.
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * The runner auto-discovers this file via Vite `import.meta.glob` in
 * `selftest/index.ts` (matches `**\/*SelfTest.ts`).
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  SpamReportCodec,
  SpamReportError,
  SPAM_REPORT_SCHEMA_VERSION,
  generateEd25519KeyPair,
} from "./SpamReport";
import type { SpamReportPayload } from "./SpamReport";
import {
  SpamReportStore,
  SPAM_REPORT_COOLDOWN_WINDOW_SECONDS,
  SPAM_REPORT_MAX_IN_WINDOW,
} from "./SpamReportStore";
import {
  reportAndBlock,
  SpamReportAndBlockError,
} from "./SpamReportAndBlockCoordinator";
import { BlockListStore } from "../block-list/BlockListStore";

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Spam report (report + block + cooldown)",
  isVerified: true,
  run: async () =>
    buildSuiteResult([
      // A. Codec
      await testSignVerifyRoundTrip(),
      await testEncodeDecodeRoundTrip(),
      await testIsReportDistinguishesSiblingPrefixes(),
      await testTamperedPayloadFailsVerify(),
      await testReporterEqualsReportedThrowsOnDecode(),
      await testInvalidMessageIdHashLengthThrows(),
      await testUnsupportedSchemaRejected(),

      // B. computeMessageIdHash
      await testHashDeterministic(),
      await testHashDiffersAcrossMessageIds(),

      // C. Store cooldown
      await testFreshStoreCanReport(),
      await testCooldownSaturates(),
      await testCooldownReleasesAfterWindow(),
      await testRecordReportReturnsFalseWhenSaturated(),

      // D. Coordinator
      await testCoordinatorHappyPath(),
      await testCoordinatorCooldownActiveThrows(),
      await testCoordinatorReporterEqualsReportedThrows(),
      await testCoordinatorBlockPayloadReasonIsSpamFlag(),
      await testCoordinatorMessageIdHashMatchesSHA256(),

      // E. Hardening invariants
      await testPayloadHasNoFreeTextReasonField(),
      await testCodecNeverSerializesRawMessageId(),
      await testReportedAtIsLocalReporterClock(),
    ]),
});

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

let dbCounter = 0;

/** Each call returns a SpamReportStore backed by a fresh, isolated IDB database. */
function freshReportStore(): SpamReportStore {
  dbCounter += 1;
  return new SpamReportStore(
    `kaether:spam-reports:selftest:${dbCounter}:${Date.now()}`,
  );
}

/** Each call returns a BlockListStore backed by a fresh, isolated IDB database. */
function freshBlockStore(): BlockListStore {
  dbCounter += 1;
  return new BlockListStore(
    `kaether:block-list:selftest:${dbCounter}:${Date.now()}`,
  );
}

async function samplePayload(
  reported: string,
  reporter: string,
): Promise<SpamReportPayload> {
  return {
    schema: SPAM_REPORT_SCHEMA_VERSION,
    reportedUserId: reported,
    reporterUserId: reporter,
    category: "spam",
    messageIdHash: await SpamReportCodec.computeMessageIdHash("msg-1"),
    reportedAt: 1_700_000_000,
  };
}

/**
 * base64url-encode a string (for hand-crafting synthetic wire payloads
 * in tests that need to bypass the sign() path).
 */
function b64urlString(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * base64-encode a Uint8Array (standard base64, matching JSON field format).
 */
function b64bytes(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary);
}

/**
 * Craft a raw wire string (bypassing sign()) so we can test decode-level
 * structural rejections that the sign() path would prevent.
 */
function craftWire(overrides: {
  schema?: number;
  reportedUserId?: string;
  reporterUserId?: string;
  category?: string;
  messageIdHashBytes?: Uint8Array;
  signatureBytes?: Uint8Array;
}): string {
  const schema = overrides.schema ?? 1;
  const reportedUserId = overrides.reportedUserId ?? "alice";
  const reporterUserId = overrides.reporterUserId ?? "bob";
  const category = overrides.category ?? "spam";
  const messageIdHashBytes =
    overrides.messageIdHashBytes ?? new Uint8Array(32).fill(0xab);
  const signatureBytes =
    overrides.signatureBytes ?? new Uint8Array(64).fill(0x00);

  const json = JSON.stringify({
    payload: {
      schema,
      reportedUserId,
      reporterUserId,
      category,
      messageIdHash: b64bytes(messageIdHashBytes),
      reportedAt: 1_700_000_000,
    },
    signature: b64bytes(signatureBytes),
  });
  return SpamReportCodec.PREFIX + b64urlString(json);
}

// ---------------------------------------------------------------------------
// A. Codec
// ---------------------------------------------------------------------------

async function testSignVerifyRoundTrip(): Promise<TestCase> {
  const name = "sign + verify round-trip";
  try {
    const { privateKeyBytes, publicKeyBytes } = await generateEd25519KeyPair();
    const payload = await samplePayload("alice", "bob");
    const report = await SpamReportCodec.sign(payload, privateKeyBytes);
    const ok =
      (await SpamReportCodec.verify(report, publicKeyBytes)) &&
      report.payload.reportedUserId === payload.reportedUserId &&
      report.payload.reporterUserId === payload.reporterUserId &&
      report.payload.category === payload.category &&
      report.payload.reportedAt === payload.reportedAt &&
      report.payload.messageIdHash.length === 32 &&
      report.signature.length === 64;
    return ok ? pass(name) : fail(name, "verify returned false or fields mismatch");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testEncodeDecodeRoundTrip(): Promise<TestCase> {
  const name = "encode + decode round-trip";
  try {
    const { privateKeyBytes } = await generateEd25519KeyPair();
    const payload = await samplePayload("alice", "bob");
    const report = await SpamReportCodec.sign(payload, privateKeyBytes);
    const encoded = SpamReportCodec.encode(report);
    const decoded = SpamReportCodec.decode(encoded);
    // Compare all fields (Uint8Array equality by value)
    const hashEq =
      decoded.payload.messageIdHash.length === report.payload.messageIdHash.length &&
      decoded.payload.messageIdHash.every(
        (b, i) => b === report.payload.messageIdHash[i],
      );
    const sigEq =
      decoded.signature.length === report.signature.length &&
      decoded.signature.every((b, i) => b === report.signature[i]);
    const ok =
      decoded.payload.schema === report.payload.schema &&
      decoded.payload.reportedUserId === report.payload.reportedUserId &&
      decoded.payload.reporterUserId === report.payload.reporterUserId &&
      decoded.payload.category === report.payload.category &&
      decoded.payload.reportedAt === report.payload.reportedAt &&
      hashEq &&
      sigEq;
    return ok ? pass(name) : fail(name, "decoded fields do not match original");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testIsReportDistinguishesSiblingPrefixes(): Promise<TestCase> {
  const name = "isReport distinguishes from sibling control prefixes";
  try {
    const { privateKeyBytes } = await generateEd25519KeyPair();
    const payload = await samplePayload("alice", "bob");
    const report = await SpamReportCodec.sign(payload, privateKeyBytes);
    const positive = SpamReportCodec.encode(report);

    const positives = [SpamReportCodec.isReport(positive)];
    const negatives = [
      SpamReportCodec.isReport("hello world"),
      SpamReportCodec.isReport("kaether:e2ee:v1:foo"),
      SpamReportCodec.isReport("kaether:receipt:v1:foo"),
      SpamReportCodec.isReport("kaether:tombstone:v1:foo"),
      SpamReportCodec.isReport("kaether:reply:v1:foo"),
      SpamReportCodec.isReport("kaether:pin:v1:foo"),
      SpamReportCodec.isReport("kaether:draft:v1:foo"),
      SpamReportCodec.isReport("kaether:block:sync:v1:foo"),
      SpamReportCodec.isReport("kaether:user:claim:v1:foo"),
      SpamReportCodec.isReport("kaether:eph:v1:foo"),
      SpamReportCodec.isReport("kaether:caption:v1:foo"),
      SpamReportCodec.isReport("kaether:voice:v1:foo"),
    ];
    const ok =
      positives.every((v) => v) && negatives.every((v) => !v);
    return ok
      ? pass(name)
      : fail(
          name,
          `positives=${JSON.stringify(positives)} negatives=${JSON.stringify(negatives)}`,
        );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testTamperedPayloadFailsVerify(): Promise<TestCase> {
  const name = "tampered payload bytes fail verify";
  try {
    const { privateKeyBytes, publicKeyBytes } = await generateEd25519KeyPair();
    const payload = await samplePayload("alice", "bob");
    const report = await SpamReportCodec.sign(payload, privateKeyBytes);

    // Build a different payload (changes the canonical signed bytes).
    // The original signature does NOT verify against it.
    const tampered: SpamReportPayload = {
      ...payload,
      category: "impersonation", // changed from "spam"
    };
    const forged = { payload: tampered, signature: report.signature };
    const verifies = await SpamReportCodec.verify(forged, publicKeyBytes);
    return !verifies
      ? pass(name)
      : fail(name, "forged report with tampered payload still verified");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testReporterEqualsReportedThrowsOnDecode(): Promise<TestCase> {
  const name = "reporter == reported throws on decode";
  try {
    const wire = craftWire({
      reportedUserId: "carol",
      reporterUserId: "carol",
    });
    try {
      SpamReportCodec.decode(wire);
      return fail(name, "decode did not throw");
    } catch (err) {
      if (
        err instanceof SpamReportError &&
        err.kind.tag === "reporterEqualsReported"
      ) {
        return pass(name);
      }
      return fail(name, `wrong error: ${err}`);
    }
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testInvalidMessageIdHashLengthThrows(): Promise<TestCase> {
  const name = "messageIdHash != 32 bytes is rejected by sign()";
  try {
    const { privateKeyBytes } = await generateEd25519KeyPair();
    const badHash = new Uint8Array(16).fill(0xcd); // half length
    const payload: SpamReportPayload = {
      schema: SPAM_REPORT_SCHEMA_VERSION,
      reportedUserId: "alice",
      reporterUserId: "bob",
      category: "spam",
      messageIdHash: badHash,
      reportedAt: 1_700_000_000,
    };
    try {
      await SpamReportCodec.sign(payload, privateKeyBytes);
      return fail(name, "sign did not throw");
    } catch (err) {
      if (
        err instanceof SpamReportError &&
        err.kind.tag === "invalidMessageIdHashLength" &&
        err.kind.length === 16
      ) {
        return pass(name);
      }
      return fail(name, `wrong error: ${err}`);
    }
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testUnsupportedSchemaRejected(): Promise<TestCase> {
  const name = "unsupported schema is rejected by decode()";
  try {
    const wire = craftWire({ schema: 99 });
    try {
      SpamReportCodec.decode(wire);
      return fail(name, "decode did not throw");
    } catch (err) {
      if (
        err instanceof SpamReportError &&
        err.kind.tag === "unsupportedSchema" &&
        err.kind.version === 99
      ) {
        return pass(name);
      }
      return fail(name, `wrong error: ${err}`);
    }
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// B. computeMessageIdHash
// ---------------------------------------------------------------------------

async function testHashDeterministic(): Promise<TestCase> {
  const name = "computeMessageIdHash deterministic from same messageId";
  try {
    const a = await SpamReportCodec.computeMessageIdHash("msg-42");
    const b = await SpamReportCodec.computeMessageIdHash("msg-42");
    const ok = a.length === 32 && b.length === 32 && a.every((v, i) => v === b[i]);
    return ok
      ? pass(name)
      : fail(name, `len=${a.length} eq=${a.every((v, i) => v === b[i])}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testHashDiffersAcrossMessageIds(): Promise<TestCase> {
  const name = "computeMessageIdHash differs across messageIds";
  try {
    const a = await SpamReportCodec.computeMessageIdHash("msg-42");
    const b = await SpamReportCodec.computeMessageIdHash("msg-43");
    const collision = a.every((v, i) => v === b[i]);
    return !collision ? pass(name) : fail(name, "collision detected");
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// C. Store cooldown
// ---------------------------------------------------------------------------

async function testFreshStoreCanReport(): Promise<TestCase> {
  const name = "fresh store canReport=true";
  try {
    const store = freshReportStore();
    const ok = await store.canReport("alice");
    return ok ? pass(name) : fail(name, "got false on fresh store");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCooldownSaturates(): Promise<TestCase> {
  const name = "cooldown saturates after maxReportsInWindow inside window";
  try {
    const store = freshReportStore();
    const base = 1_700_000_000;
    // Record 10 reports inside the same window
    for (let i = 0; i < SPAM_REPORT_MAX_IN_WINDOW; i++) {
      await store.recordReport("alice", base + i);
    }
    // 11th attempt while still inside the window (base + 9 < base + 10)
    const canStill = await store.canReport("alice", base + 9);
    return !canStill
      ? pass(name)
      : fail(name, "canReport=true after saturation");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCooldownReleasesAfterWindow(): Promise<TestCase> {
  const name = "cooldown window expiry restores capacity";
  try {
    const store = freshReportStore();
    const base = 1_700_000_000;
    for (let i = 0; i < SPAM_REPORT_MAX_IN_WINDOW; i++) {
      await store.recordReport("alice", base + i);
    }
    // Last record was at base + 9.  Window is 10s. At base + 11 + 10 = base + 21
    // every recorded ts satisfies (base+21) - ts >= 10, so they all age off.
    const canAfter = await store.canReport(
      "alice",
      base + SPAM_REPORT_COOLDOWN_WINDOW_SECONDS + 11,
    );
    return canAfter
      ? pass(name)
      : fail(name, "canReport=false after window expiry");
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testRecordReportReturnsFalseWhenSaturated(): Promise<TestCase> {
  const name = "recordReport returns false when cooldown active";
  try {
    const store = freshReportStore();
    const base = 1_700_000_000;
    for (let i = 0; i < SPAM_REPORT_MAX_IN_WINDOW; i++) {
      await store.recordReport("alice", base + i);
    }
    // Extra attempt while still in window
    const recorded = await store.recordReport("alice", base + 9);
    return !recorded
      ? pass(name)
      : fail(name, "extra report was recorded despite saturation");
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// D. Coordinator
// ---------------------------------------------------------------------------

async function testCoordinatorHappyPath(): Promise<TestCase> {
  const name = "coordinator happy path returns both payloads + installs block";
  try {
    const { privateKeyBytes } = await generateEd25519KeyPair();
    const blockStore = freshBlockStore();
    const reportStore = freshReportStore();
    const outcome = await reportAndBlock(
      "bob",
      privateKeyBytes,
      "alice",
      "harassment",
      "msg-99",
      { blockStore, reportStore, nowSeconds: 1_700_000_000 },
    );
    const isBlocked = await blockStore.isBlocked("alice");
    const prefixOk = outcome.reportShippedPayload.startsWith(
      SpamReportCodec.PREFIX,
    );
    const blockAddrOk = outcome.blockPayload.blockedUserId === "alice";
    const ok = isBlocked && prefixOk && blockAddrOk;
    return ok
      ? pass(name)
      : fail(
          name,
          `blocked=${isBlocked} prefix=${prefixOk} addr=${blockAddrOk}`,
        );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCoordinatorCooldownActiveThrows(): Promise<TestCase> {
  const name = "coordinator throws cooldownActive when ledger saturated + no block installed";
  try {
    const { privateKeyBytes } = await generateEd25519KeyPair();
    const blockStore = freshBlockStore();
    const reportStore = freshReportStore();
    const base = 1_700_000_000;

    // Saturate the ledger
    for (let i = 0; i < SPAM_REPORT_MAX_IN_WINDOW; i++) {
      await reportStore.recordReport("alice", base + i);
    }

    try {
      await reportAndBlock(
        "bob",
        privateKeyBytes,
        "alice",
        "spam",
        "msg-1",
        { blockStore, reportStore, nowSeconds: base + 9 },
      );
      return fail(name, "reportAndBlock did not throw");
    } catch (err) {
      if (
        err instanceof SpamReportAndBlockError &&
        err.kind.tag === "cooldownActive"
      ) {
        // Critical: no block should have been installed
        const blocked = await blockStore.isBlocked("alice");
        return !blocked
          ? pass(name)
          : fail(name, "block was installed despite cooldown throw");
      }
      return fail(name, `wrong error: ${err}`);
    }
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCoordinatorReporterEqualsReportedThrows(): Promise<TestCase> {
  const name = "coordinator throws reporterEqualsReported on self-report";
  try {
    const { privateKeyBytes } = await generateEd25519KeyPair();
    const blockStore = freshBlockStore();
    const reportStore = freshReportStore();

    try {
      await reportAndBlock(
        "carol",
        privateKeyBytes,
        "carol",
        "other",
        "msg-1",
        { blockStore, reportStore },
      );
      return fail(name, "reportAndBlock did not throw");
    } catch (err) {
      if (
        err instanceof SpamReportAndBlockError &&
        err.kind.tag === "reporterEqualsReported"
      ) {
        // Also verify no state mutation
        const blocked = await blockStore.isBlocked("carol");
        const recorded = await reportStore.report("carol");
        const ok = !blocked && recorded === undefined;
        return ok
          ? pass(name)
          : fail(
              name,
              `blocked=${blocked} recorded=${recorded !== undefined}`,
            );
      }
      return fail(name, `wrong error: ${err}`);
    }
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCoordinatorBlockPayloadReasonIsSpamFlag(): Promise<TestCase> {
  const name = 'coordinator block payload reason is "spamFlag"';
  try {
    const { privateKeyBytes } = await generateEd25519KeyPair();
    const blockStore = freshBlockStore();
    const reportStore = freshReportStore();
    const outcome = await reportAndBlock(
      "bob",
      privateKeyBytes,
      "alice",
      "harassment",
      "msg-99",
      { blockStore, reportStore },
    );
    const wireReason = outcome.blockPayload.reason;
    const diskEntry = await blockStore.entry("alice");
    const diskReason = diskEntry?.reason;
    const ok = wireReason === "spamFlag" && diskReason === "spamFlag";
    return ok
      ? pass(name)
      : fail(
          name,
          `wire=${wireReason} disk=${diskReason}`,
        );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCoordinatorMessageIdHashMatchesSHA256(): Promise<TestCase> {
  const name = "coordinator messageIdHash equals SHA256(messageId)";
  try {
    const { privateKeyBytes } = await generateEd25519KeyPair();
    const blockStore = freshBlockStore();
    const reportStore = freshReportStore();
    const messageId = "msg-99";
    const expected = await SpamReportCodec.computeMessageIdHash(messageId);

    const outcome = await reportAndBlock(
      "bob",
      privateKeyBytes,
      "alice",
      "spam",
      messageId,
      { blockStore, reportStore },
    );
    const report = SpamReportCodec.decode(outcome.reportShippedPayload);
    const ok =
      report.payload.messageIdHash.length === 32 &&
      report.payload.messageIdHash.every((b, i) => b === expected[i]);
    return ok
      ? pass(name)
      : fail(
          name,
          `got ${report.payload.messageIdHash.length}B hash, expected SHA256 match`,
        );
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// E. Hardening invariants
// ---------------------------------------------------------------------------

async function testPayloadHasNoFreeTextReasonField(): Promise<TestCase> {
  const name =
    "SpamReportPayload exposes only categorical fields (no free-text reason)";
  try {
    const payload = await samplePayload("alice", "bob");
    // Runtime shape check: enumerate the exact keys of the payload object.
    // This catches any future accidental addition of `reason`, `reasonText`,
    // `comment`, `freeText`, etc.
    const actualKeys = new Set(Object.keys(payload));
    const expectedKeys = new Set([
      "schema",
      "reportedUserId",
      "reporterUserId",
      "category",
      "messageIdHash",
      "reportedAt",
    ]);

    const extraKeys = [...actualKeys].filter((k) => !expectedKeys.has(k));
    const missingKeys = [...expectedKeys].filter((k) => !actualKeys.has(k));

    // Also explicitly assert no free-text variants exist
    const hasFreeText = [
      "reason",
      "reasonText",
      "freeText",
      "comment",
      "note",
      "description",
    ].some((k) => actualKeys.has(k));

    const ok =
      extraKeys.length === 0 && missingKeys.length === 0 && !hasFreeText;
    return ok
      ? pass(name)
      : fail(
          name,
          `extra=${JSON.stringify(extraKeys)} missing=${JSON.stringify(missingKeys)} hasFreeText=${hasFreeText}`,
        );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCodecNeverSerializesRawMessageId(): Promise<TestCase> {
  const name = "codec never serializes the raw messageId (only the hash)";
  try {
    const { privateKeyBytes } = await generateEd25519KeyPair();
    const messageId = "DISTINCTIVE-PLAINTEXT-MSGID-DO-NOT-LEAK";
    const payload: SpamReportPayload = {
      schema: SPAM_REPORT_SCHEMA_VERSION,
      reportedUserId: "alice",
      reporterUserId: "bob",
      category: "spam",
      messageIdHash: await SpamReportCodec.computeMessageIdHash(messageId),
      reportedAt: 1_700_000_000,
    };
    const report = await SpamReportCodec.sign(payload, privateKeyBytes);
    const encoded = SpamReportCodec.encode(report);

    // 1. Raw string must not appear in the encoded wire form
    const inEncoded = encoded.includes(messageId);

    // 2. Must not appear in the base64url-decoded JSON either
    const b64 = encoded.slice(SpamReportCodec.PREFIX.length);
    let padded = b64.replace(/-/g, "+").replace(/_/g, "/");
    padded += "=".repeat((4 - (padded.length % 4)) % 4);
    const jsonStr = new TextDecoder().decode(
      Uint8Array.from(atob(padded), (c) => c.charCodeAt(0)),
    );
    const inJson = jsonStr.includes(messageId);

    const ok = !inEncoded && !inJson;
    return ok
      ? pass(name)
      : fail(name, `inEncoded=${inEncoded} inJson=${inJson}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testReportedAtIsLocalReporterClock(): Promise<TestCase> {
  const name = "reportedAt is the LOCAL reporter clock (not server-supplied)";
  try {
    const { privateKeyBytes } = await generateEd25519KeyPair();
    const blockStore = freshBlockStore();
    const reportStore = freshReportStore();
    const localClock = 1_234_567_890;

    const outcome = await reportAndBlock(
      "bob",
      privateKeyBytes,
      "alice",
      "spam",
      "msg-1",
      { blockStore, reportStore, nowSeconds: localClock },
    );
    const report = SpamReportCodec.decode(outcome.reportShippedPayload);
    const ok = report.payload.reportedAt === localClock;
    return ok
      ? pass(name)
      : fail(name, `got reportedAt=${report.payload.reportedAt}, want ${localClock}`);
  } catch (e) {
    return fail(name, String(e));
  }
}
